import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionNav from '../../../components/layout/SectionNav';
import { researchTabs } from '../../../nav/publicNav';
import { Loader2, Target, Search, ChevronDown, ChevronUp, X } from 'lucide-react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import ExportButton from '../../../components/ui/ExportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

function ScoreRing({ score, size = 80 }) {
  const radius = size / 2 - 6;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 60 ? '#22c55e' : score >= 35 ? '#fbbf24' : '#ef4444';

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke="hsl(var(--muted))" strokeWidth={5} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={color} strokeWidth={5} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold" style={{ color }}>{Math.round(score)}</span>
      </div>
    </div>
  );
}

function ScoreBar({ label, score, max, color }) {
  const pct = Math.min((score / max) * 100, 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{score.toFixed(1)}/{max}</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function StockScoringPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [scoreDetail, setScoreDetail] = useState(null);
  const [rankedList, setRankedList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/stock-scores?page_size=50&sort=desc`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRankedList(data.items || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, []);

  const fetchDetail = useCallback(async (symbol) => {
    setDetailLoading(true);
    setSelectedSymbol(symbol);
    try {
      const res = await fetch(`${API}/stock-score?symbol=${encodeURIComponent(symbol)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setScoreDetail(data);
    } catch (e) { setError(e.message); }
    setDetailLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const radarData = scoreDetail ? [
    { axis: 'Fundamental', value: scoreDetail.sub_scores.fundamental.score, max: 25 },
    { axis: 'Valuation', value: scoreDetail.sub_scores.valuation.score, max: 25 },
    { axis: 'Institutional', value: scoreDetail.sub_scores.institutional.score, max: 25 },
    { axis: 'Insider', value: scoreDetail.sub_scores.insider.score, max: 15 },
    { axis: 'Sentiment', value: scoreDetail.sub_scores.sentiment.score, max: 10 },
  ].map(d => ({ ...d, normalized: (d.value / d.max) * 100 })) : [];

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) fetchDetail(search.trim().toUpperCase());
  };

  return (
    <div className="space-y-4">
      <SectionNav tabs={researchTabs} />
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Target className="w-5 h-5 text-emerald-500" /> AI Stock Score
        </h1>
        <p className="text-xs text-muted-foreground">Proprietary scoring combining fundamentals, valuation, institutional flows, insider activity & sentiment</p>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Enter symbol (e.g. RELIANCE)"
            className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-emerald-500" />
        </div>
        <Button type="submit" size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">Score</Button>
        {selectedSymbol && (
          <Button variant="ghost" size="sm" className="h-8" onClick={() => { setSelectedSymbol(null); setScoreDetail(null); }}>
            <X className="w-3.5 h-3.5" />
          </Button>
        )}
      </form>

      {detailLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : scoreDetail && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-6">
              <ScoreRing score={scoreDetail.total_score} size={100} />
              <div>
                <h2 className="font-bold text-lg">{scoreDetail.company_name}</h2>
                <p className="text-sm text-muted-foreground">{scoreDetail.symbol} · {scoreDetail.sector}</p>
                <Badge variant={scoreDetail.total_score >= 60 ? 'default' : scoreDetail.total_score >= 35 ? 'secondary' : 'destructive'}
                  className="mt-1 text-[10px]">
                  {scoreDetail.total_score >= 60 ? 'Strong' : scoreDetail.total_score >= 35 ? 'Moderate' : 'Weak'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <ResponsiveContainer width="100%" height={200}>
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="80%">
                  <PolarGrid className="stroke-muted" />
                  <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10 }} className="fill-muted-foreground" />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  <Radar dataKey="normalized" stroke="#22c55e" fill="#22c55e" fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="md:col-span-2">
            <CardContent className="p-4 space-y-3">
              <ScoreBar label="Fundamental (ROE, OPM, NPM)" score={scoreDetail.sub_scores.fundamental.score} max={25} color="#22c55e" />
              <ScoreBar label="Valuation (PE, PB vs Sector)" score={scoreDetail.sub_scores.valuation.score} max={25} color="#3b82f6" />
              <ScoreBar label="Institutional (FII, DII, Promoter)" score={scoreDetail.sub_scores.institutional.score} max={25} color="#8b5cf6" />
              <ScoreBar label="Insider Activity (90d trades)" score={scoreDetail.sub_scores.insider.score} max={15} color="#f59e0b" />
              <ScoreBar label="Sentiment (X coverage)" score={scoreDetail.sub_scores.sentiment.score} max={10} color="#ec4899" />
            </CardContent>
          </Card>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold">Top Scored Stocks</h2>
          <ExportButton rows={rankedList} filename="stock-scores" />
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-emerald-800 text-white">
                  <th className="text-center py-2 px-2 font-semibold text-[10px] uppercase w-12">#</th>
                  <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Company</th>
                  <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Sector</th>
                  <th className="text-center py-2 px-3 font-semibold text-[10px] uppercase">Score</th>
                  <th className="text-center py-2 px-2 font-semibold text-[10px] uppercase">Fund.</th>
                  <th className="text-center py-2 px-2 font-semibold text-[10px] uppercase">Val.</th>
                  <th className="text-center py-2 px-2 font-semibold text-[10px] uppercase">Inst.</th>
                </tr>
              </thead>
              <tbody>
                {rankedList.map((r, i) => (
                  <tr key={i} className="border-b border-muted/20 hover:bg-muted/20 cursor-pointer"
                    onClick={() => fetchDetail(r.symbol)}>
                    <td className="py-2 px-2 text-center text-muted-foreground">{i + 1}</td>
                    <td className="py-2 px-3">
                      <span className="font-medium">{r.company_name}</span>
                      <span className="text-muted-foreground ml-1">{r.symbol}</span>
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">{r.sector || '—'}</td>
                    <td className="py-2 px-3 text-center">
                      <Badge variant={r.total_score >= 60 ? 'default' : r.total_score >= 35 ? 'secondary' : 'destructive'}
                        className="text-[10px] px-2">
                        {r.total_score}
                      </Badge>
                    </td>
                    <td className="py-2 px-2 text-center text-muted-foreground">{r.fundamental}</td>
                    <td className="py-2 px-2 text-center text-muted-foreground">{r.valuation}</td>
                    <td className="py-2 px-2 text-center text-muted-foreground">{r.institutional}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
